// ---------------------------------------------------------------------------
// Categories and products: reads and writes.
//
// Every mutation invalidates the caches it affects, so a product saved on the
// management screen is immediately correct on the POS grid without a reload.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';
import type {
  Category,
  PriceHistoryEntry,
  PrintRouting,
  Product,
  ProductListResponse,
} from '../types';

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function useCategories(options?: { includeInactive?: boolean; enabled?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;

  return useQuery({
    queryKey: [...queryKeys.catalog.categories, { includeInactive }],
    queryFn: async () => {
      const response = await api.get<{ categories: Category[] }>(
        `/categories${includeInactive ? '?includeInactive=true' : ''}`,
      );
      return response.categories;
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export interface CategoryPayload {
  nameEn: string;
  nameAr: string;
  // No slug: the category's code (cat-001) is numbered by the shop.
  /// The letters its product codes start with (BEF). Empty: chosen by the shop.
  codePrefix?: string;
  color?: string | null;
  icon?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  /// Where this category's products print when they are sold. Sent only by
  /// somebody holding `category.print_routing_manage`; the server refuses it
  /// from anybody else rather than ignoring it.
  ///
  /// OPTIONAL ON UPDATE ONLY. Leaving it out of a save that changes a name or
  /// a colour is how the server is told to leave the routing alone. Creating
  /// a category without it is refused - see `CreateCategoryPayload`.
  printRouting?: PrintRouting;
  /// Which printer prepares this category's items. Required alongside
  /// KITCHEN_AND_CASHIER, refused alongside CASHIER_ONLY - both enforced by
  /// the server as well as by the form.
  preparationPrinterId?: string | null;
}

/// What CREATING a category needs, which is one field more than updating one.
///
/// The destination is required here and typed as such, so a call site that
/// forgets it fails to compile rather than failing at the counter. The server
/// enforces the same rule; this is the layer that makes it hard to get wrong.
export type CreateCategoryPayload = CategoryPayload & { printRouting: PrintRouting };

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCategoryPayload) =>
      api.post<{ category: Category; message: string }>('/categories', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.categories });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: CategoryPayload & { id: string }) =>
      api.put<{ category: Category; message: string }>(`/categories/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.categories });
      // A renamed category shows on every product row, so those go too.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/categories/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.categories });
    },
  });
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export interface ProductFilters {
  search?: string;
  categoryId?: string;
  /// The catalogue screen's "show retired products too" switch.
  includeInactive?: boolean;
  sort?: 'name' | 'price' | 'stock' | 'recent';
  /// The quick-pricing filters: only option products, only discounted ones.
  hasVariants?: boolean;
  hasDiscount?: boolean;
  page?: number;
  pageSize?: number;
}

function buildProductQuery(filters: ProductFilters): string {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.includeInactive) params.set('includeInactive', 'true');
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.hasVariants) params.set('hasVariants', 'true');
  if (filters.hasDiscount) params.set('hasDiscount', 'true');
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 50));

  return params.toString();
}

/// The code the next category WOULD get (cat-015) - shown in the form
/// before saving. A preview: the code is decided at save time.
export function useNextCategoryCode(enabled = true) {
  return useQuery({
    queryKey: ['categories', 'next-code'],
    queryFn: () => api.get<{ code: string }>('/categories/next-code'),
    enabled,
    staleTime: 0,
  });
}

/// The code and barcode the next product in a category WOULD get - shown in
/// the form before saving. A preview: the numbers are decided at save time.
export function useNextProductNumbers(categoryId: string, enabled = true) {
  return useQuery({
    queryKey: ['products', 'next-numbers', categoryId],
    queryFn: () =>
      api.get<{ sku: string; barcode: string }>(`/products/next-numbers?categoryId=${encodeURIComponent(categoryId)}`),
    enabled: enabled && Boolean(categoryId),
    staleTime: 0,
  });
}

export function useProducts(filters: ProductFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalog.products(filters as Record<string, unknown>),
    queryFn: () => api.get<ProductListResponse>(`/products?${buildProductQuery(filters)}`),
    enabled,
    staleTime: 30_000,
    // Keeps the previous page on screen while the next one loads, so the grid
    // does not blink to empty every time the search box changes.
    placeholderData: (previous) => previous,
  });
}

export function useProduct(id: string | null) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await api.get<{ product: Product }>(`/products/${id}`);
      return response.product;
    },
    enabled: Boolean(id),
  });
}

export function usePriceHistory(productId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['product', productId, 'price-history'],
    queryFn: async () => {
      const response = await api.get<{ history: PriceHistoryEntry[] }>(
        `/products/${productId}/price-history`,
      );
      return response.history;
    },
    enabled: Boolean(productId) && enabled,
  });
}

/// The payload the API expects. Money and weight are strings.
export interface ProductPayload {
  nameEn: string;
  nameAr: string;
  // No sku and no barcode: the shop numbers both when the product is saved.
  categoryId: string;
  imageUrl?: string | null;
  sellingUnit: string;
  /// Null clears the cost ("not recorded"); omitted leaves it untouched.
  costPrice?: string | null;
  sellingPrice?: string;
  wholesalePrice?: string | null;
  minSellingPrice?: string | null;
  /// THE PRICES, in whole lira, exactly as the owner typed them. These are
  /// what the form sends and what the server treats as authoritative; the
  /// dollar fields above are still accepted so the spreadsheet import keeps
  /// working, and are derived from these when both are known.
  ///
  /// Null clears the price. `sellingPriceLbp: null` is what a product with
  /// options sends: it has no price of its own.
  costPriceLbp?: string | null;
  sellingPriceLbp?: string | null;
  wholesalePriceLbp?: string | null;
  minSellingPriceLbp?: string | null;
  trackStock?: boolean;
  currentStock?: string;
  minStock?: string;
  preparationNotes?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  /// True when the cashier must choose a type before selling this product.
  hasVariants?: boolean;
  /// The product's own percentage promotion. Never a second price.
  discountEnabled?: boolean;
  discountPercent?: string | null;
  discountStartsAt?: string | null;
  discountEndsAt?: string | null;
  /// The WHOLE list as the owner left the form. Rows with an id are updated,
  /// rows without are added, and any variant no longer here is retired -
  /// never deleted, because old receipts still name it. Omitted means "do not
  /// touch the variants".
  variants?: Array<{
    id?: string;
    nameEn: string;
    nameAr: string;
    /// The option's exact lira price - what the till charges for it.
    sellingPriceLbp: string;
    isActive: boolean;
  }>;
  priceChangeReason?: string | null;
}

function invalidateProducts(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['products'] });
  void queryClient.invalidateQueries({ queryKey: ['product'] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.categories });
  // Product counts and low-stock warnings appear on the dashboard.
  void queryClient.invalidateQueries({ queryKey: queryKeys.system.dashboard });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProductPayload) =>
      api.post<{ product: Product; message: string }>('/products', payload),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<ProductPayload> & { id: string }) =>
      api.put<{ product: Product; message: string }>(`/products/${id}`, payload),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/products/${id}`),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Image upload
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Where products sit inside a category
// ---------------------------------------------------------------------------

/// The display order a new product in this category would be given if the
/// owner does not choose one: ten past the highest, so it lands at the end
/// with room in front of it. The Add Product form starts from this, so adding
/// a product is one less decision without ever being a locked position.
export function useNextProductOrder(categoryId: string | undefined) {
  return useQuery({
    queryKey: ['categories', categoryId, 'next-product-position'],
    queryFn: async () =>
      (await api.get<{ position: number }>(`/categories/${categoryId}/next-product-position`))
        .position,
    enabled: Boolean(categoryId),
    staleTime: 10_000,
  });
}

/// Save the display orders typed on the category order screen.
export function useReorderCategoryProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      orders,
    }: {
      categoryId: string;
      orders: Array<{ productId: string; sortOrder: number }>;
    }) =>
      api.post<{ products: Array<{ id: string; sortOrder: number }>; message: string }>(
        `/categories/${categoryId}/product-order`,
        { orders },
      ),
    retry: false,
    onSuccess: () => {
      // Both the management list and the till read the same order.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}
